import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  TrendingUp,
  Users2,
  HandCoins,
  Award,
  ShieldCheck,
  Briefcase,
  Quote,
  Cpu,
  GraduationCap,
  LineChart,
  Heart,
  Lock,
  Sparkles,
  CheckCircle2,
  Mail,
  Phone,
} from "lucide-react";
import { PolicyHeader } from "@/components/PolicyHeader";
import { SITE_IMAGES } from "@/data/site-images";
import { safeHtml } from "@/lib/sanitize";

const SIX_PILLARS = [
  {
    n: "01",
    icon: Heart,
    title: "Customer Excellence",
    body: "Every customer deserves personalized financial guidance. We continuously develop customized financial solutions based on individual goals, business requirements, financial capacity, and long-term aspirations. Our success is measured by customer satisfaction — not transaction volume.",
  },
  {
    n: "02",
    icon: Cpu,
    title: "Technology & Innovation",
    body: "Investing in Artificial Intelligence, digital documentation, secure cloud infrastructure, automation, business intelligence, data analytics, CRM, cybersecurity and digital financial ecosystems — enabling faster, safer, and more efficient services.",
  },
  {
    n: "03",
    icon: Building2,
    title: "Nationwide Expansion",
    body: "Strong presence across India through corporate offices, regional offices, branches, associate partners, agency partners, DSAs, strategic alliances and digital service centers — reaching both urban and rural India.",
  },
  {
    n: "04",
    icon: GraduationCap,
    title: "Professional Excellence",
    body: "Knowledge creates value. Our professionals continuously improve through financial training, leadership development, professional certifications, regulatory updates, digital skills and customer-service excellence — ensuring world-class service quality.",
  },
  {
    n: "05",
    icon: LineChart,
    title: "Sustainable Business Growth",
    body: "Every financial decision is evaluated for long-term sustainability — responsible lending, risk assessment, customer protection, business stability, operational efficiency, financial discipline and governance excellence.",
  },
  {
    n: "06",
    icon: Sparkles,
    title: "Social Responsibility",
    body: "Active support for financial literacy, entrepreneurship development, MSME growth, women empowerment, tribal development, rural development, employment generation, youth skill development and community welfare.",
  },
];

const STAKEHOLDER_COMMITMENTS = [
  "Complete Transparency",
  "Ethical Business Practices",
  "Professional Financial Guidance",
  "Customer-Centric Solutions",
  "Long-Term Relationship Management",
  "Responsible Financial Services",
  "Technology-Driven Innovation",
  "Secure Digital Infrastructure",
  "Sustainable Business Development",
  "Continuous Improvement",
];

const TRUST_REASONS = [
  "Honest Advice",
  "Transparent Communication",
  "Ethical Business Conduct",
  "Professional Documentation",
  "Secure Processes",
  "Fast Customer Support",
  "Customized Financial Solutions",
  "Dedicated Relationship Managers",
  "Strong Compliance Framework",
  "Long-Term Customer Relationships",
];

const GOVERNANCE = [
  "Transparency",
  "Accountability",
  "Compliance",
  "Ethics",
  "Internal Controls",
  "Risk Management",
  "Information Security",
  "Data Privacy",
  "Responsible Leadership",
  "Continuous Monitoring",
];

const ECOSYSTEM = [
  ["Personal Finance", false],
  ["Business Finance", false],
  ["Project Finance", false],
  ["MSME Finance", false],
  ["Agriculture Finance", false],
  ["Investment Advisory", true],
  ["Wealth Management", true],
  ["Mutual Fund Distribution", true],
  ["Insurance Support", true],
  ["Corporate Consultancy", false],
  ["Taxation & Compliance", false],
  ["Accounting Services", false],
  ["Legal Documentation", false],
  ["Startup Advisory", false],
  ["Business Strategy", false],
  ["Partnership Consulting", false],
];

const FUTURE_GOALS = [
  ["1M+", "Customers"],
  ["₹10,000+ Cr", "Financial solutions facilitated"],
  ["Pan-India", "Coverage across every major state"],
  ["AI-First", "Digital financial platforms"],
  ["Largest", "Financial partner network"],
  ["Thousands", "Employment opportunities"],
];

const PARTNER_BENEFITS = [
  "Trusted Financial Partner",
  "Professional Advisory",
  "Transparent Processes",
  "Ethical Business Practices",
  "Innovative Financial Solutions",
  "Experienced Professionals",
  "Long-Term Relationship",
  "Nationwide Growth Vision",
  "Customer-First Philosophy",
  "Sustainable Financial Excellence",
];

const PARTNER_TYPES = [
  "Strategic Investors",
  "Institutional Partners",
  "Corporate Organizations",
  "Financial Institutions",
  "NBFCs",
  "Banks",
  "Insurance Companies",
  "Venture Capital Firms",
  "Private Equity Firms",
  "Family Offices",
  "Government Organizations",
  "International Business Partners",
];

function Section({ eyebrow, title, lead, children, testId }) {
  return (
    <section data-testid={testId} className="py-16 md:py-20 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        {eyebrow && (
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            {eyebrow}
          </div>
        )}
        {title && (
          <h2 className="font-serif text-3xl md:text-4xl text-white max-w-3xl leading-tight">
            {title}
          </h2>
        )}
        {lead && (
          <p className="mt-4 text-base text-white/75 max-w-3xl leading-relaxed">{lead}</p>
        )}
        <div className="mt-10">{children}</div>
      </div>
    </section>
  );
}

export default function Investors() {
  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <PolicyHeader
        eyebrow="Investor Relations"
        title={
          <>
            Building <em className="not-italic text-[#D4AF37]">Long-Term Value</em> Through Trust,{" "}
            <span className="font-script italic text-[#F0D788]">Transparency</span> &amp;
            Sustainable Growth
          </>
        }
        lead="At Sahyog Finance & Noble Infinity Asset Management Private Limited, lasting success is built on trust, transparency, responsible governance and sustainable financial growth — creating long-term value for every stakeholder."
      />

      {/* Why choose us as financial growth partner */}
      <Section
        testId="investor-why"
        eyebrow="Why Choose Us as Your Financial Growth Partner"
        title="More than a service provider — a partner that supports your journey with integrity"
      >
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 items-start">
          <div className="space-y-5 text-base text-white/80 leading-relaxed">
            <p>
              Choosing the right financial institution is choosing a long-term partner that
              understands your financial aspirations and supports your journey with integrity and
              professionalism. At Sahyog Finance &amp; Noble Infinity, we focus on building
              relationships that last for generations.
            </p>
            <p>
              Our goal is to become a trusted financial ecosystem where customers, entrepreneurs,
              investors and business partners can confidently access comprehensive financial
              solutions under one roof.
            </p>
          </div>
          <div className="rounded-3xl overflow-hidden border border-[#D4AF37]/25">
            <img
              src={SITE_IMAGES.heroes[9]}
              alt="Long-term financial growth partnership"
              loading="lazy"
              data-testid="investor-why-image"
              className="w-full h-[240px] md:h-[300px] object-cover"
            />
          </div>
        </div>
      </Section>

      {/* Business philosophy */}
      <Section testId="investor-philosophy" eyebrow="Our Business Philosophy">
        <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] via-[#0c2654] to-[#04102B] p-8 md:p-12 max-w-4xl">
          <Quote size={28} className="text-[#D4AF37] mb-5" />
          <div className="font-serif text-2xl md:text-3xl leading-snug text-white">
            "Trust Creates Relationships.
            <br />
            Relationships Create Opportunities.
            <br />
            <span className="text-[#F0D788]">Opportunities Create Wealth.</span>"
          </div>
          <p className="mt-7 text-sm md:text-base text-white/70 leading-relaxed">
            Sustainable financial success comes from responsible decision-making, ethical
            business practices, customer satisfaction, innovation and continuous improvement.
            Rather than focusing only on short-term growth, we are committed to building a
            financially strong organization capable of delivering consistent value over decades.
          </p>
        </div>
      </Section>

      {/* Stakeholder commitments */}
      <Section
        testId="investor-stakeholder"
        eyebrow="Our Commitment to Every Stakeholder"
        title="Whether you are a customer, business owner, advisor, DSA, partner or institution — you become part of our journey"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {STAKEHOLDER_COMMITMENTS.map((c, i) => (
            <div
              key={c}
              data-testid={`stakeholder-${i}`}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <ShieldCheck size={14} className="text-[#D4AF37] mt-1 shrink-0" />
              <div className="text-sm text-white/85">{c}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Six strategic pillars */}
      <Section
        testId="investor-pillars"
        eyebrow="Our Strategic Growth Model"
        title="Six fundamental pillars driving long-term growth"
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SIX_PILLARS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`pillar-${i}`}
              className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6 hover:border-[#D4AF37]/45 transition"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 grid place-items-center rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25">
                  <p.icon size={18} className="text-[#F0D788]" />
                </div>
                <div className="text-2xl font-serif text-[#D4AF37]/70">{p.n}</div>
              </div>
              <h3 className="font-serif text-lg text-white mb-2">{p.title}</h3>
              <p className="text-sm text-white/70 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Why customers trust us */}
      <Section testId="investor-trust" eyebrow="Why Customers Trust Us" title="Earning trust every day">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TRUST_REASONS.map((t, i) => (
            <div
              key={t}
              data-testid={`trust-${i}`}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <CheckCircle2 size={14} className="text-[#D4AF37] mt-1 shrink-0" />
              <div className="text-sm text-white/85">{t}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Governance */}
      <Section
        testId="investor-governance"
        eyebrow="Corporate Governance"
        title="Strong governance creates strong institutions"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {GOVERNANCE.map((g, i) => (
            <div
              key={g}
              data-testid={`gov-${i}`}
              className="rounded-xl border border-[#D4AF37]/15 bg-[#0A1F44] px-4 py-3 text-center"
            >
              <Lock size={14} className="text-[#F0D788] mx-auto mb-2" />
              <div className="text-sm text-white/85">{g}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Financial ecosystem */}
      <Section
        testId="investor-ecosystem"
        eyebrow="Our Financial Ecosystem"
        title="One integrated platform — multiple financial & business solutions"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {ECOSYSTEM.map(([name, regulated], i) => (
            <div
              key={name}
              data-testid={`eco-${i}`}
              className="flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <Briefcase size={13} className="text-[#D4AF37] mt-1 shrink-0" />
              <div className="text-sm text-white/85">
                {name}
                {regulated && <span className="text-[#F0D788]">*</span>}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-white/55">
          * Subject to applicable registrations and regulatory approvals.
        </p>
      </Section>

      {/* Future vision */}
      <Section
        testId="investor-future"
        eyebrow="Our Vision for the Future"
        title="What we are building over the next decade"
      >
        <div className="mb-8 rounded-3xl overflow-hidden border border-[#D4AF37]/20">
          <img
            src={SITE_IMAGES.heroes[4]}
            alt="Future of Sahyog Finance · pan-India expansion"
            loading="lazy"
            data-testid="investor-future-image"
            className="w-full h-[180px] md:h-[240px] object-cover"
          />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FUTURE_GOALS.map(([n, label], i) => (
            <div
              key={`future-${n}-${i}`}
              data-testid={`future-${i}`}
              className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-6"
            >
              <div className="font-serif text-3xl text-[#F0D788]">{n}</div>
              <div className="mt-2 text-sm text-white/80">{label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Why partner */}
      <Section
        testId="investor-partner-benefits"
        eyebrow="Why Partner With Sahyog Finance & Noble Infinity"
        title="Every partnership should create mutual growth"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PARTNER_BENEFITS.map((b, i) => (
            <div
              key={b}
              data-testid={`partner-benefit-${i}`}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <Sparkles size={14} className="text-[#D4AF37] mt-1 shrink-0" />
              <div className="text-sm text-white/85">{b}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Partner types */}
      <Section
        testId="investor-partner-types"
        eyebrow="Investor & Strategic Partner Information"
        title="We welcome collaboration opportunities with"
      >
        <div className="flex flex-wrap gap-2">
          {PARTNER_TYPES.map((p) => (
            <span
              key={p}
              className="px-3 py-1.5 rounded-full border border-[#D4AF37]/25 bg-[#0A1F44] text-xs text-white/85"
            >
              {p}
            </span>
          ))}
        </div>
      </Section>

      {/* Connect with us */}
      <Section testId="investor-connect" eyebrow="Connect With Our Investor Relations Team">
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <div className="space-y-4 text-base text-white/80 leading-relaxed max-w-2xl">
            <p>
              Our Investor Relations Team is committed to maintaining transparent communication
              with stakeholders and providing timely information regarding our corporate
              initiatives, business growth, governance standards, strategic partnerships and
              long-term vision.
            </p>
            <p>
              We welcome meaningful discussions that contribute to innovation, sustainable
              growth and mutual success.
            </p>
          </div>
          <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A1F44] p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-4">
              Direct Contact
            </div>
            <div className="space-y-3">
              <a
                href="mailto:info.nobleinfinity@gmail.com"
                data-testid="investor-email"
                className="flex items-center gap-3 text-sm text-white/85 hover:text-[#F0D788]"
              >
                <Mail size={15} className="text-[#D4AF37]" /> info.nobleinfinity@gmail.com
              </a>
              <a
                href="tel:+917016975324"
                data-testid="investor-phone"
                className="flex items-center gap-3 text-sm text-white/85 hover:text-[#F0D788]"
              >
                <Phone size={15} className="text-[#D4AF37]" /> +91 70169 75324
              </a>
            </div>
            <div className="mt-6 text-xs text-white/55">
              Sahyog Finance &amp; Noble Infinity Asset Management Pvt. Ltd.<br />
              A-106, Ambaji Complex, Vyara, Tapi, Gujarat 394650
            </div>
          </div>
        </div>
      </Section>

      {/* Investor Portal preview */}
      <Section
        testId="investor-portal-preview"
        eyebrow="Investor Portal"
        title="Track your portfolio anytime, from anywhere"
      >
        <div
          data-testid="investor-portal-hero"
          className="rounded-3xl overflow-hidden border border-[#D4AF37]/40 bg-gradient-to-br from-[#0e2a6b] via-[#1c3d92] to-[#0A1F44] p-8 md:p-12"
        >
          <div className="grid md:grid-cols-[1.1fr_1fr] gap-8 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#F0D788] mb-4">
                Secure &middot; Transparent &middot; Profitable
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight">
                Target Return:{" "}
                <span className="text-[#F0D788]">up to 11.55% p.a.</span>
              </h3>
              <ul className="mt-6 space-y-2.5 text-sm md:text-base text-white/90">
                {[
                  "Automatic Digital Agreement generation with e-Signature",
                  "Secure Investor login &amp; password reset facility",
                  "Real-time portfolio tracking &amp; performance dashboard",
                  "Monthly earnings reports &amp; commission calculator",
                  "Instant document downloads (Agreement, Tax, KYC)",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <CheckCircle2 size={17} className="text-[#F0D788] mt-0.5 shrink-0" />
                    <span dangerouslySetInnerHTML={safeHtml(f)} />
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/investor/login"
                  data-testid="investor-portal-login-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
                >
                  Investor Login <ArrowRight size={14} />
                </Link>
                <a
                  href="mailto:info.nobleinfinity@gmail.com?subject=Investor%20onboarding%20enquiry"
                  data-testid="investor-portal-contact-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-gold text-sm"
                >
                  Talk to Investor Relations
                </a>
              </div>
            </div>
            <div className="rounded-2xl bg-black/25 border border-white/10 p-6 space-y-4">
              <div className="text-xs uppercase tracking-[0.25em] text-[#F0D788]">
                After onboarding you receive
              </div>
              {[
                "Instant digital agreement",
                "Secure login credentials",
                "Full access to your portfolio dashboard",
                "Priority Investor Relations support",
              ].map((r) => (
                <div key={r} className="flex items-start gap-3 text-sm text-white/85">
                  <span className="w-6 h-6 grid place-items-center rounded-full bg-[#D4AF37]/20 text-[#F0D788] text-[10px] font-semibold shrink-0">
                    ✓
                  </span>
                  {r}
                </div>
              ))}
            </div>
          </div>
          <p className="mt-8 pt-6 border-t border-white/15 text-[11px] text-white/60 leading-relaxed">
            <strong className="text-white/80">Disclaimer:</strong> Indicative target return.
            Actual returns are subject to market and business risk. Not guaranteed. Please read
            the offer document carefully before investing.
          </p>
        </div>
      </Section>

      {/* Promise */}
      <Section testId="investor-promise" eyebrow="Our Promise">
        <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] via-[#0c2654] to-[#04102B] p-8 md:p-14 max-w-4xl">
          <Quote size={26} className="text-[#D4AF37] mb-5" />
          <div className="font-serif text-xl md:text-2xl leading-snug text-white">
            "We don't measure success only by financial performance. We measure success by the
            trust we earn, the businesses we empower, the wealth we help create, the lives we
            improve, and the lasting relationships we build."
          </div>
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/about"
            data-testid="investor-about-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-gold text-sm"
          >
            About Us <ArrowRight size={14} />
          </Link>
          <Link
            to="/governance"
            data-testid="investor-governance-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
          >
            Read Our Governance Policy
          </Link>
        </div>
      </Section>

      {/* Disclaimer */}
      <section className="py-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <p className="text-[11px] text-white/45 leading-relaxed">
            <strong className="text-white/65">Disclaimer:</strong> The information provided on
            this page is intended solely for general corporate and stakeholder communication. It
            does not constitute an offer, invitation, recommendation or solicitation to invest in
            the Company or any financial product. All financial, investment, wealth management,
            insurance, mutual fund, advisory or regulated services are offered only in
            accordance with applicable laws, regulatory approvals, registrations and licensing
            requirements. The Company is committed to maintaining the highest standards of
            corporate governance, transparency, compliance and ethical business practices.
          </p>
        </div>
      </section>
    </div>
  );
}
